import { z } from 'zod'
import { widgetSchema } from '../../common/schema'
import { lenientObjectGroup } from '../../common/zod-lenient'
import {
  explorerColorSchema,
  explorerLightColors,
  explorerDarkColors,
} from '../../common/card-colors'
import { cardSkinFields } from '../../common/property-card/card-skin-fields'
import { cardTagsSettingSchema } from '../explorer/config'

/**
 * Filter for the related-properties widget.
 *
 * `property` holds the source property's slug — the one we want related items for.
 * In a playground / standalone preview it must be set explicitly. When the widget
 * renders inside the property detail page, the renderer overrides this with the
 * current property slug at SSR time via the same `pageLevelFilterOverrides`
 * pipeline used by description, gallery, calendar, etc.
 */
// Plain object (not a widgetFilterSchema.extend): the base's `feed_id` was
// never consumed — the fetch keys on the composite-id feedId argument — so
// #748 retired it, and the remaining base fields were already overridden here.
export const relatedPropertiesFilterSchema = z.object({
  platform: z.string().nullish().describe('Platform UUID (optional)'),
  property: z
    .string()
    .optional()
    .describe('Source property slug — overridden by the PDP renderer at SSR time'),
  limit: z.number().int().min(1).max(24).default(3).describe('Maximum cards to show'),
  // Optional passthroughs to the central applyFilters() helper
  types: z.string().optional().describe('Comma-separated property type IDs'),
  amenities: z.string().optional().describe('Comma-separated amenity IDs'),
  guests: z.number().int().optional().describe('Minimum guest count'),
})
export type RelatedPropertiesFilter = z.infer<typeof relatedPropertiesFilterSchema>

export const relatedPropertiesWidgetConfig = widgetSchema.extend({
  filter: relatedPropertiesFilterSchema,

  // Section heading is provided by the shared `section` field on the base
  // widgetSchema. We override its default title here so admins see a sensible
  // pre-filled value (the previous default text from the legacy `title` field).
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('You might also like').describe('Section title text'),
  }).default({ show: false, title: 'You might also like' }).describe('Section heading shown above the widget content'),

  template: z.enum(['grid', 'slider']).default('grid').describe('Layout template'),

  grid: z
    .object({
      columns: z.number().int().min(1).max(4).default(1).describe('Columns at mobile (< 600px)'),
      columns780: z
        .number()
        .int()
        .min(1)
        .max(4)
        .default(2)
        .describe('Columns at tablet (600–959px)'),
      columns960: z
        .number()
        .int()
        .min(1)
        .max(4)
        .default(3)
        .describe('Columns at desktop (960px+)'),
      gap: z.number().default(16).describe('Gap between cards in pixels'),
    })
    .default({ columns: 1, columns780: 2, columns960: 3, gap: 16 })
    .describe('Grid layout settings'),

  slider: z
    .object({
      hideArrows: z.boolean().default(false).describe('Hide navigation arrows'),
      hideDots: z.boolean().default(false).describe('Hide dot indicators'),
      autoplay: z.boolean().default(false).describe('Enable autoplay'),
      autoplayInterval: z
        .number()
        .int()
        .min(1000)
        .max(30000)
        .default(5000)
        .describe('Autoplay interval in milliseconds'),
      loop: z.boolean().default(true).describe('Loop back to start'),
    })
    .default({ hideArrows: false, hideDots: false, autoplay: false, autoplayInterval: 5000, loop: true })
    .describe('Slider layout settings'),

  // Static-only price display (no checkin/checkout context on PDP).
  // `withDates` is included only to satisfy PropertyCard's type contract; it is never
  // actually consulted at runtime because we never pass checkin/checkout via visitorFilters.
  priceDisplay: z
    .object({
      noDates: z
        .enum(['base', 'lowest', 'none'])
        .default('base')
        .describe('Price display mode'),
      withDates: z
        .enum(['average', 'total', 'per_night', 'none'])
        .default('per_night')
        .describe('(unused — kept for PropertyCard compatibility)'),
    })
    .default({ noDates: 'base', withDates: 'per_night' })
    .describe('Price display settings'),

  noItemsText: z.string().optional().describe('Message when no related properties are found'),

  // Reuse explorer's color schema verbatim so PropertyCard's CSS variable contract resolves.
  lightModeColors: explorerColorSchema(explorerLightColors)
    .default(explorerLightColors)
    .describe('Colors in light mode'),
  darkModeColors: explorerColorSchema(explorerDarkColors)
    .default(explorerDarkColors)
    .describe('Colors in dark mode'),

  /**
   * Card skin (#902 bullet 5). This widget renders the explorer's card
   * component (common/property-card/TemplateExplorer) but had no way to style
   * it: `buildCardSkinCss` hard-scoped every rule to `.hfse-explorer-container`,
   * which never wraps `.hr-related`. Spreading the SAME `cardSkinFields` the
   * explorer spreads means the two surfaces cannot drift, and the group keeps
   * the neutral contract — every default here is the neutral value, so an
   * existing widget that has never been re-saved emits no card CSS at all and
   * renders byte-for-byte what it rendered before.
   *
   * `subtitleFontSize` ships here even though the subtitle node only renders
   * from `card.subtitleSource`, a feed-level explorer setting (#562) this widget
   * has no equivalent of: the dead-setting sentinel resolves an imported object
   * passed to `z.object(...)` but not a destructuring-rest copy of it or an
   * `.omit()` (dead-setting-sentinel/analyzer.ts:70, :124-133), so the group is
   * taken whole and the dashboard form hides that one leaf, the same way it
   * already hides the unmapped colour keys. The explorer's five `loadMore*`
   * leaves are absent for a different reason — they are declared in the
   * explorer's own config, not in the shared set, because they style the
   * grid's Load More button rather than a card.
   *
   * `lenientObjectGroup(...)`: the neutral state is an EMPTY object, and PHP's
   * json_encode serializes `{}` as `[]` — so a widget saved with no card knob
   * touched comes back as `card: []`, which a bare z.object rejects with
   * "Expected object, received array". The render path is already covered
   * (parseWidgetConfig's stripNullSettingLeaves maps `[]` to `{}` for an
   * object-typed key, widget-core schema.ts), but every DIRECT parse is not —
   * the dashboard form validates with `schema.safeParse` (zod-form form.tsx),
   * so without a wrapper such a widget could never be saved again. It goes one
   * step past the explorer's `phpObject` groups on purpose: this key is NEW on
   * this widget, so whatever garbage a blob already carried under `card` was
   * silently stripped before #902 and must degrade to neutral now, not throw.
   * `.default({})` and `.describe()` both sit INSIDE per the wrapper's contract
   * (docs/widget-system.md, #1168: zod-form's child-wins def merge lets the
   * wrapper's undefined description clobber an outer one): the group must be
   * OPTIONAL in stored settings (every widget saved before #902 has no `card`
   * key at all), and `{}` then fills each leaf from its own neutral default.
   *
   * `cardTags` (#1819): the card component has resolved `card.cardTags`
   * since #1815, but this group stripped the key at parse, so the PDP strip
   * never showed a pill the grid showed. Declared as the SAME render-boundary
   * object the explorer declares (`cardTagsSettingSchema`), so the two
   * surfaces cannot drift; absent = no pills = today's card.
   */
  card: lenientObjectGroup(z.object({ ...cardSkinFields, cardTags: cardTagsSettingSchema }).default({}).describe('Card appearance')),
})

export type RelatedPropertiesWidgetConfig = z.infer<typeof relatedPropertiesWidgetConfig>
